import React from "react";

import NoSpeakerIcon from "@uui/UserCircle";

import Box, { BoxProps } from "@mui/material/Box";
import styled from "@mui/material/styles/styled";
import Typography from "@mui/material/Typography";

import { SUPPRESSED_SPEAKER_NAMES } from "@app/analytics/MeetingReport/constants";
import { ELLIPSIS_SX } from "@app/common/styles";
import type { AnnotationDirection } from "@app/common/types";
import { getInitials } from "@app/common/utils";

import Avatar, { AvatarProps } from "@rui/Avatar";
import { Column, Row, StackProps } from "@rui/Stack";

type TurnProps = StackProps & { nested: boolean };

export const Turn = styled<React.FC<TurnProps>>(({ nested: _n, ...props }) => (
  <Row {...props} />
))(({ nested, theme }) => ({
  maxWidth: "100%",
  paddingTop: nested ? theme.spaces.xsmall : theme.spaces.base,

  [theme.breakpoints.up("mobile")]: {
    paddingLeft: theme.spaces.base,
    paddingRight: theme.spaces.base,
  },
}));

export const DeletedTurn = styled<React.FC<TurnProps>>(
  ({ nested: _n, children, ...props }) => (
    <Row {...props}>
      <Row className="transcript-turn--deleted">{children}</Row>
    </Row>
  )
)(({ nested, theme }) => ({
  paddingTop: nested ? theme.spaces.xsmall : theme.spaces.base,
  width: "100%",
  paddingLeft: nested ? "46px" : 0,

  [theme.breakpoints.up("mobile")]: {
    paddingLeft: nested ? "60px" : theme.spaces.base,
    paddingRight: theme.spaces.base,
  },

  "& .transcript-turn__edited-by": {
    transition: "all 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
    opacity: 0,
  },

  "& .transcript-turn--deleted": {
    width: "100%",
    justifyContent: "space-between",
    columnGap: theme.spaces.base,
    padding: `${theme.spaces.xxsmall} ${theme.spaces.small}`,
    borderRadius: theme.corners.small,
    border: "1px dashed",
    borderColor: theme.palette.transparent.black[8],
    backgroundColor: theme.palette.transparent.black[2],
    transition: "all 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
    fontSize: "0.75rem",
    fontWeight: 500,
    color: theme.palette.shades.neutral[400],
    whiteSpace: "nowrap",

    "&:hover": {
      cursor: "default",
      backgroundColor: theme.palette.transparent.black[4],
      color: theme.palette.shades.neutral[600],
      "& .transcript-turn__edited-by": {
        opacity: 1,
      },
    },
  },
}));

type EditableSpeakerAvatarProps = AvatarProps & {
  isEditable?: boolean;
};

type UnknownSpeakerAvatarProps = Omit<
  EditableSpeakerAvatarProps,
  "children" | "name"
>;

const UnknownSpeakerAvatar = styled(
  ({ isEditable: _, ...props }: UnknownSpeakerAvatarProps) => (
    <Avatar {...props} sx={{ bgcolor: "shades.neutral.50" }}>
      <NoSpeakerIcon />
    </Avatar>
  )
)(({ isEditable, theme }) => ({
  "& .MuiSvgIcon-root": {
    fontSize: "16px",
    color: theme.palette.shades.neutral[400],
  },
  backgroundColor: theme.palette.shades.neutral[50],
  fontSize: "0.6875rem",
  cursor: isEditable ? "pointer" : "default",
  "&:hover": {
    border: isEditable
      ? `2px solid ${theme.palette.shades.primary[500]}`
      : "none",
  },
}));

const EditableSpeakerAvatar = styled(
  ({ isEditable: _, ...props }: EditableSpeakerAvatarProps) => (
    <Avatar {...props} />
  )
)(({ theme, isEditable }) => ({
  fontSize: "0.6875rem",
  cursor: isEditable ? "pointer" : "default",
  "&:hover": {
    border: isEditable
      ? `2px solid ${theme.palette.shades.primary[500]}`
      : "none",
  },
}));

const NoSpeakerShim = styled(Row)({
  width: "32px", // width of the avatar,
});

export const SpeakerName = styled(Typography)(({ theme }) => ({
  ...theme.typography.detailMed,
  ...ELLIPSIS_SX,
  color: theme.palette.shades.neutral[600],
}));

export const Speaker = ({
  speaker,
  isEditable,
  onClick,
}: {
  speaker?: string | null;
  isEditable?: boolean;
  onClick?: React.MouseEventHandler;
}) => {
  if (speaker) {
    if (SUPPRESSED_SPEAKER_NAMES.includes(speaker)) {
      return <UnknownSpeakerAvatar onClick={onClick} isEditable={isEditable} />;
    }

    return (
      <EditableSpeakerAvatar
        onClick={onClick}
        name={speaker}
        isEditable={isEditable}
      >
        {getInitials(speaker)}
      </EditableSpeakerAvatar>
    );
  }

  return speaker === null ? (
    <UnknownSpeakerAvatar onClick={onClick} isEditable={isEditable} />
  ) : (
    <NoSpeakerShim />
  );
};

export const ContentColumn = styled(Column)({
  width: "calc(100% - 48px)", // 48 = 32px for avatar + 16px spacing
  maxWidth: "calc(100% - 48px)",
});

export const ContentColumnHeader = styled(Row)(({ theme }) => ({
  width: "100%",
  maxWidth: "100%",
  gap: theme.spacing(2),
}));

export const SpeakerContainer = styled(Row)(({ theme }) => ({
  flexShrink: 1,
  overflow: "hidden",
  gap: theme.spacing(1),
  [theme.breakpoints.up("mobile")]: {
    gap: theme.spacing(2),
  },
}));

export const Timestamp = styled(Typography)(({ theme }) => ({
  ...theme.typography.detailReg,
  color: theme.palette.shades.neutral[400],
}));

export const SpeechBubbleOuterContainer = styled(Column)(
  ({ onClick, theme }) => ({
    position: "relative",
    width: "fit-content",
    maxWidth: "100%",

    "&:hover": {
      cursor: onClick ? "pointer" : "default",
      "& .transcript-turn__speech-bubble": {
        backgroundColor: onClick ? theme.palette.shades.ghost[500] : undefined,
      },
      "& .transcript-turn__copy-button": {
        display: "flex",
      },
    },
  })
);

type SpeechBubbleProps = StackProps & { active: boolean };

export const SpeechBubbleInnerContainer = styled<React.FC<SpeechBubbleProps>>(
  ({ active: _a, ...props }) => <Column {...props} />
)(({ active, theme }) => ({
  ...theme.typography.bodyPrimaryReg,
  fontSize: "inherit", // we support changing the font size in the settings
  fontFamily: theme.typography.fontFamilySecondary,
  padding: theme.spaces.small,
  width: "fit-content",
  maxWidth: "100%",
  backgroundColor: active
    ? theme.palette.shades.ghost[500]
    : theme.palette.shades.ghost[300],
  color: theme.palette.shades.neutral[600],
  borderRadius: theme.corners.base,
  borderTopLeftRadius: "1px",
  scrollMarginTop: "100px",
  transition: "all 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
  border: "1px solid",
  borderColor: active ? theme.palette.shades.primary[500] : "transparent",
}));

type IndicatorProps = BoxProps & { type: AnnotationDirection };

export const AnnotationIndicator = styled<React.FC<IndicatorProps>>(
  ({ type: _t, ...props }) => <Box {...props} />
)(({ theme, type }) => ({
  backgroundColor:
    type === "positive"
      ? theme.palette.shades.positive[300]
      : theme.palette.shades.negative[300],
  height: "6px",
  width: "6px",
  borderRadius: theme.corners.round,
}));

export const AnnotationLabel = styled(Typography)(({ theme }) => ({
  fontFamily: theme.typography.fontFamilySecondary,
  fontSize: "0.8125rem",
  color: theme.palette.shades.neutral[500],
}));
